/**
 * D-EZ-102 / D-EZ-95 — WHAT THE PAPER KNOWS ABOUT THE WINTER.
 *
 * The season edition and the winter edition both print the league's longer
 * story: the awards, the records that fell, who retired, who goes into the
 * Hall of Fame, who was lost, and the sponsor news on the top cars. Every item
 * is a fold over the settled seasons (`story/yearInReview.ts`) and the winter's
 * carried contracts — the same rule as `paperFacts.ts`: if it is not in here,
 * no edition can print it.
 *
 * ⚠ FACTS, NOT PROSE. Nothing in this file is a sentence the paper prints.
 */

import type { HistorySeason } from '../history/index.ts';
import { sortedSeasons } from '../history/index.ts';
import { sponsorSlotsFor } from '../finance/sponsorNames.ts';
import { yearInReview } from '../story/yearInReview.ts';
import type { PaperFacts } from './paperFacts.ts';

export interface PaperWinter {
  seasonNumber: number;
  champion: { name: string; titles: number; wins: number; points: number } | null;
  awards: { title: string; name: string; basis: string }[];
  recordsBroken: {
    title: string;
    scope: 'season' | 'career';
    name: string;
    value: number;
    previousName: string | null;
    previousValue: number | null;
    /** Records about money are in Megabucks (1 Mb = $10,000). */
    money: boolean;
  }[];
  retirements: { name: string; age: number | null; seasons: number; wins: number; championships: number; starts: number }[];
  hallOfFame: { name: string; citation: string; wins: number; championships: number; starts: number }[];
  lost: { name: string; raceNumber: number }[];
  sponsorNews: { name: string; sponsor: string; kind: 'new-primary' | 'staying'; seasons: number }[];
}

/** How many top drivers' sponsor news is worth printing. */
const SPONSOR_NEWS_TOP = 10;

export function winterFacts(input: readonly HistorySeason[], seasonNumber: number): PaperWinter | null {
  const review = yearInReview(input, seasonNumber, { arc: false });
  if (!review) return null;
  const season = sortedSeasons(input).find((s) => s.seasonNumber === seasonNumber);
  const contracts = season?.offseason?.carry?.finance.contracts ?? {};
  const top = season?.season.finalStandings.drivers.slice(0, SPONSOR_NEWS_TOP) ?? [];
  const nextRoster = new Set((season?.offseason?.roster ?? []).map((c) => c.driverId));
  const sponsorNews: PaperWinter['sponsorNews'] = [];
  for (const d of top) {
    if (!nextRoster.has(d.driverId)) continue;
    const dfs = contracts[d.driverId];
    if (!dfs?.primary?.sponsorName) continue;
    const primary = sponsorSlotsFor(dfs).find((s) => s.slot === 'Primary');
    if (!primary) continue;
    if (dfs.primary.signedInWinter) {
      sponsorNews.push({ name: d.name, sponsor: primary.name, kind: 'new-primary', seasons: 1 });
    } else if ((dfs.primary.seasonsHeld ?? 1) >= 2) {
      // Next season will be one more than the seasons already held.
      sponsorNews.push({ name: d.name, sponsor: primary.name, kind: 'staying', seasons: (dfs.primary.seasonsHeld ?? 1) + 1 });
    }
  }
  return {
    seasonNumber,
    champion: review.champion
      ? { name: review.champion.name, titles: review.champion.titles, wins: review.champion.wins, points: review.champion.points }
      : null,
    awards: review.awards.map((a) => ({ title: a.title, name: a.name, basis: a.basis })),
    recordsBroken: review.recordsBroken.map((r) => ({
      title: r.title,
      scope: r.scope,
      name: r.name,
      value: r.value,
      previousName: r.previous?.name ?? null,
      previousValue: r.previous?.value ?? null,
      money: /won|winnings/i.test(r.title),
    })),
    retirements: review.retirements.map((r) => ({
      name: r.name,
      age: r.age,
      seasons: r.seasons,
      wins: r.wins,
      championships: r.championships,
      starts: r.starts,
    })),
    hallOfFame: review.hallOfFame.map((h) => ({
      name: h.name,
      citation: h.citation,
      wins: h.wins,
      championships: h.championships,
      starts: h.starts,
    })),
    lost: review.lost.map((l) => ({ name: l.name, raceNumber: l.raceNumber })),
    sponsorNews: sponsorNews.slice(0, 6),
  };
}

/**
 * The season edition with the league's winter news attached, or the WINTER
 * edition itself (`edition: 'winter'`) — the same facts, a different paper.
 */
export function withWinter(
  facts: PaperFacts,
  seasons: readonly HistorySeason[],
  edition: 'season' | 'winter' = 'season',
): PaperFacts {
  const winter = winterFacts(seasons, facts.season);
  if (!winter) return facts;
  return { ...facts, edition, winter };
}
